import splitNames, { splitDelegated } from '../on/_splitnames.js';
import checkObjectType from '../_helpers/checkobjecttype.js';
import forOwn from '../_helpers/forown.js';
import forEach from '../_helpers/foreach.js';
import defs from '../_core/defs.js';
import removeListener from './_removelistener.js';
import cancelIfGone from './_cancelifgone.js';
import undelegateListener from './_undelegatelistener.js';
import triggerOne from '../trigger/_triggerone.js';
import { unlisten } from '../_dom/index.js';
import { dropOwner, hasOwner } from '../on/_owners.js';
import isSameCallback from '../_helpers/issamecallback.js';
import getProp from '../_helpers/getprop.js';
import toArray from '../_helpers/toarray.js';
import assign from '../_helpers/assign.js';
var delegatedKeyReg = /^_change:delegated:(.*)$/;
// returns objects which a delegated listener reaches through given delegation event name
function getDelegationTargets(object, def, name) {
  var _delegatedKeyReg$exec;
  if (name === '_asterisk:add') {
    // the event lives on a Matreshka.Array
    return toArray(object);
  }
  if (name === '_asterisk:set') {
    return Object.keys(def.keys).map(function (key) {
      return object[key];
    });
  }
  var key = (_delegatedKeyReg$exec = delegatedKeyReg.exec(name)) == null ? void 0 : _delegatedKeyReg$exec[1];
  return key === undefined ? null : [getProp(object, key)];
}
// the names on() was given for the object's own plain and DOM events
function userEventNames(def) {
  var names = [];
  var add = function add(name) {
    if (names.indexOf(name) === -1) names.push(name);
  };
  forOwn(def.events, function (events, name) {
    if (name[0] !== '_') add(name);else if (name.indexOf('_bind:') === 0) forEach(events, function (_ref) {
      var info = _ref.info;
      return info.domEvent && add(info.domEvent.name);
    });
  });
  return names;
}
// the delegated names ("a.b@x", "*@x") on() was given for this object, with the callback and context
function userDelegations(def, callback, context) {
  var found = new Map();
  forOwn(def.events, function (events, name) {
    var _delegatedKeyReg$exec2;
    var key = name === '_asterisk:add' || name === '_asterisk:set' ? '*' : (_delegatedKeyReg$exec2 = delegatedKeyReg.exec(name)) == null ? void 0 : _delegatedKeyReg$exec2[1];
    if (key === undefined) return;
    forEach(events, function (event) {
      var _event$info = event.info,
        delegatedData = _event$info.delegatedData,
        pathStr = _event$info.pathStr;
      if (delegatedData.info.user && hasOwner(event, null) && (!callback || isSameCallback(callback, delegatedData.callback)) && (!context || context === delegatedData.context)) {
        var path = pathStr ? [key].concat(pathStr.split('.')) : [key];
        found.set(path.join('.') + "@" + delegatedData.name, [path, delegatedData.name]);
      }
    });
  });
  var result = [];
  found.forEach(function (value) {
    result.push(value);
  });
  return result;
}
// removes listeners added by on/once/onDebounce and keeps the ones the framework adds (bindings, calc etc)
// every event list is split in one pass, so removing many listeners stays linear
function removeUserListeners(object, def) {
  var allEvents = def.events,
    props = def.props;
  var removed = [];
  var undelegations = [];
  var retained = [];
  forOwn(assign({}, allEvents), function (events, name) {
    var isDelegation = name.indexOf('_change:delegated:') === 0 || name.indexOf('_asterisk:') === 0;
    var retain = [];
    forEach(events, function (event) {
      var _info$delegatedData;
      var info = event.info;
      var isUserEvent = isDelegation ? (_info$delegatedData = info.delegatedData) == null ? void 0 : _info$delegatedData.info.user : info.user;
      // a listener a parent's delegation (or another owner) keeps stays; this object's own ownership goes
      if (!isUserEvent || !hasOwner(event, null) || !dropOwner(event, null)) {
        retain.push(event);
      } else if (isDelegation) {
        var targets = getDelegationTargets(object, def, name);
        if (targets) {
          undelegations.push({
            targets: targets,
            delegatedData: info.delegatedData
          });
        }
      } else if (info.domEvent) {
        // _bind:KEY and _unbind:KEY share one domEvent, so it's handled once
        if (name.indexOf('_bind:') === 0) {
          var _props$name$slice;
          var domEvent = info.domEvent;
          var bindings = (_props$name$slice = props[name.slice(6)]) == null ? void 0 : _props$name$slice.bindings;
          if (bindings) {
            forEach(bindings, function (_ref2) {
              var node = _ref2.node;
              return unlisten(node, domEvent.eventName, domEvent.selector, domEvent.domEventHandler);
            });
          }
          removed.push({
            name: domEvent.name,
            callback: domEvent.callback,
            context: domEvent.context
          });
        }
      } else if (name[0] !== '_') {
        removed.push({
          name: name,
          callback: event.callback,
          context: event.context
        });
      }
    });
    retained.push({
      name: name,
      events: events,
      length: events.length,
      retain: retain
    });
  });
  // removeevent fires before the lists change, so its own listeners hear it
  forEach(removed, function (removeEventData) {
    triggerOne(object, "removeevent:" + removeEventData.name, removeEventData);
    triggerOne(object, 'removeevent', removeEventData);
  });
  forEach(retained, function (_ref3) {
    var name = _ref3.name,
      events = _ref3.events,
      length = _ref3.length,
      retain = _ref3.retain;
    // keep listeners which removeevent handlers have added meanwhile
    var current = allEvents[name] === events ? retain.concat(events.slice(length)) : retain;
    if (current.length) {
      allEvents[name] = current;
    } else {
      delete allEvents[name];
    }
  });
  // a pending onDebounce call doesn't fire after its listener is gone
  forEach(retained, function (_ref4) {
    var events = _ref4.events,
      retain = _ref4.retain;
    forEach(events, function (event) {
      if (retain.indexOf(event) === -1) {
        var _event$info2 = event.info,
          domEvent = _event$info2.domEvent,
          delegatedData = _event$info2.delegatedData;
        cancelIfGone(allEvents, domEvent ? domEvent.callback : delegatedData ? delegatedData.callback : event.callback);
      }
    });
  });
  forEach(undelegations, function (_ref5) {
    var targets = _ref5.targets,
      delegatedData = _ref5.delegatedData;
    var path = delegatedData.path,
      name = delegatedData.name,
      callback = delegatedData.callback,
      context = delegatedData.context,
      info = delegatedData.info;
    forEach(targets, function (target) {
      if (target && typeof target === 'object') {
        undelegateListener(target, path, name, callback, context, info);
      }
    });
  });
}
export default function off(object, givenNames, callback, context) {
  if (typeof this === 'object' && this.isMatreshka) {
    // when context is Matreshka instance, use this as an object and shift other args
    context = callback;
    callback = givenNames;
    givenNames = object;
    object = this;
  } else {
    // throw error when object type is wrong
    checkObjectType(object, 'off');
  }
  var isNamesVarArray = Array.isArray(givenNames);
  var def = defs.get(object);
  // allow to pass name-handler object
  // TODO: Name-handler object passed to off method is non-documented feature
  if (givenNames && typeof givenNames === 'object' && !isNamesVarArray) {
    forOwn(givenNames, function (namesObjCallback, namesObjName) {
      return off(object, namesObjName, namesObjCallback, callback);
    });
    return object;
  }
  // an object without a definition has no listeners
  if (!def) {
    return object;
  }
  if (!givenNames && !callback && !context) {
    removeUserListeners(object, def);
    return object;
  }
  // TODO: Array of names passed to off method is non-documented feature
  // split by spaces
  // no names with a callback or a context: every plain and DOM event of the object
  var names = isNamesVarArray ? givenNames : givenNames ? splitNames(givenNames) : userEventNames(def);
  // no names: the delegated events too
  if (!givenNames) {
    forEach(userDelegations(def, callback, context), function (_ref6) {
      var path = _ref6[0],
        delegatedName = _ref6[1];
      return undelegateListener(object, path, delegatedName, callback, context);
    });
  }
  forEach(names, function (name) {
    var delegated = splitDelegated(name);
    if (delegated) {
      var path = delegated[0],
        delegatedName = delegated[1];
      undelegateListener(object, path, delegatedName, callback, context);
    } else {
      removeListener(object, name, callback, context);
    }
  });
  return object;
}